// Typed client for the daily Aptitude drill.
// Server: student-agent-api/routers/aptitude_drill_router.py mounted at
// /student/aptitude-drill.

import api from './api';
import { MediaItem } from '../components/MicroLearning/MediaDisplay';

export interface DrillOption {
  label: string;
  text: string;
}

export interface DrillQuestion {
  question_id: string;
  question_text: string;
  topic: string | null;
  subtopic: string | null;
  difficulty_level: string | null;
  year_ec: number | null;
  has_media: boolean;
  options: DrillOption[];
  media?: MediaItem[];
  /** Set by the spaced-review blend (Daily Focus) — drives the 🔁 chip. */
  is_review?: boolean;
}

export interface DrillProgress {
  answered: number;
  correct: number;
  answered_ids: string[];
}

export interface DrillPathInfo {
  day_index: number;
  strategy_title: string | null;
  topic: string | null;
}

export interface DrillTodayResponse {
  session_id: string;
  drill_date: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  total_questions: number;
  questions: DrillQuestion[];
  progress: DrillProgress;
  path: DrillPathInfo | null;
}

export interface PathDay {
  day_index: number;
  topic: string;
  strategy_title: string;
  status: 'done' | 'today' | 'upcoming';
}

export interface PathOverviewResponse {
  days: PathDay[];
  current_day: number;
  total_days: number;
  completed_days: number;
}

export interface PathDayDetailResponse {
  day_index: number;
  topic: string;
  strategy_title: string;
  strategy_summary_md: string;
  status: 'done' | 'today' | 'upcoming';
  questions: DrillQuestion[];
}

export interface DrillAnswerResponse {
  is_correct: boolean;
  correct_answer: string;
  explanation: string;
  topic: string | null;
  progress: DrillProgress;
  completed: boolean;
}

export interface DrillTopicBreakdown {
  answered: number;
  correct: number;
}

export interface DrillCompleteResponse {
  session_id: string;
  drill_date: string;
  status: string;
  score: number;
  total: number;
  by_topic: Record<string, DrillTopicBreakdown>;
  weakest_today: { topic: string; correct: number; answered: number } | null;
}

export interface DrillHistoryItem {
  session_id: string;
  drill_date: string | null;
  status: 'in_progress' | 'completed' | 'abandoned';
  topic: string | null;
  path_day_index: number | null;
  strategy_title: string | null;
  total: number;
  answered: number;
  correct: number;
  score_pct: number;
  started_at: string | null;
  completed_at: string | null;
}

export interface DrillHistoryResponse {
  sessions: DrillHistoryItem[];
  count: number;
}

export interface DrillReviewQuestion extends DrillQuestion {
  correct_answer: string;
  explanation: string;
  student_answer: string | null;
  is_correct: boolean;
  answered_at: string | null;
}

export interface DrillSessionDetailResponse {
  session_id: string;
  drill_date: string | null;
  status: string;
  topic: string | null;
  path_day_index: number | null;
  strategy_title: string | null;
  total: number;
  correct: number;
  score_pct: number;
  questions: DrillReviewQuestion[];
  started_at: string | null;
  completed_at: string | null;
}

export const aptitudeDrillAPI = {
  today: async (): Promise<DrillTodayResponse> => {
    const response = await api.get<DrillTodayResponse>('/student/aptitude-drill/today');
    return response.data;
  },

  answer: async (
    question_id: string,
    selected_answer: string,
    time_spent_seconds: number,
  ): Promise<DrillAnswerResponse> => {
    const response = await api.post<DrillAnswerResponse>('/student/aptitude-drill/answer', {
      question_id,
      selected_answer,
      time_spent_seconds,
    });
    return response.data;
  },

  complete: async (): Promise<DrillCompleteResponse> => {
    const response = await api.post<DrillCompleteResponse>('/student/aptitude-drill/complete');
    return response.data;
  },

  path: async (): Promise<PathOverviewResponse> => {
    const response = await api.get<PathOverviewResponse>('/student/aptitude-drill/path');
    return response.data;
  },

  pathDay: async (day: number): Promise<PathDayDetailResponse> => {
    const response = await api.get<PathDayDetailResponse>(
      `/student/aptitude-drill/path/${day}`,
    );
    return response.data;
  },

  history: async (limit: number = 50): Promise<DrillHistoryResponse> => {
    const response = await api.get<DrillHistoryResponse>(
      '/student/aptitude-drill/history',
      { params: { limit } },
    );
    return response.data;
  },

  sessionDetail: async (sessionId: string): Promise<DrillSessionDetailResponse> => {
    const response = await api.get<DrillSessionDetailResponse>(
      `/student/aptitude-drill/sessions/${sessionId}`,
    );
    return response.data;
  },
};
